
import { useEffect, useState } from 'react';
import { LOCAL_STORAGE_KEY } from 'constants/localStorage';

import { addHoursToDate } from './helpers';
import { defaultMode, modes } from './modes';
import { Mode, NormalizeMode } from './types';

const watchedKeys: string[] = [LOCAL_STORAGE_KEY.EMAIL_VERIFICATION_DATE];

const generateModes = (currentDate: Date): NormalizeMode[] => {
  const activeModes: Mode[] = modes.filter(mode => {
    if (mode.relativeDate) {
      const relativeDateValue = mode.relativeDate();

      const startDate = mode.dateStart ? mode.dateStart(relativeDateValue) : null;
      const endDate = mode.dateEnd ? mode.dateEnd(relativeDateValue) : null;

      if (startDate && endDate && currentDate >= startDate && currentDate <= endDate) {
        return true;
      }
    }

    return false;
  });

  const normalizeActiveModes = activeModes.map(mode => {
    if (!mode.relativeDate) {
      return {
        ...mode,
        dateStart: null,
        dateEnd: null
      };
    }

    const relativeDateValue = mode.relativeDate();

    const startDate = mode.dateStart ? mode.dateStart(relativeDateValue) : null;
    const endDate = mode.dateEnd ? mode.dateEnd(relativeDateValue) : null;

    return {
      ...mode,
      dateStart: startDate,
      dateEnd: endDate
    };
  });
  // @ts-ignore
  return normalizeActiveModes;
};

export const getClosestDate = (
  currentDate: Date
): { closestDateStart: Date | null; closestDateEnd: Date | null } => {
  let closestDateStart: Date | null = null;
  let closestDateEnd: Date | null = null;

  const oneDayLater = addHoursToDate(currentDate, 24);

  modes.forEach(mode => {
    if (!mode.dateStart || !mode.dateEnd || !mode.relativeDate) {
      return;
    }

    const modeDateStart = mode.dateStart(mode.relativeDate());
    const modeDateEnd = mode.dateEnd(mode.relativeDate());

    if (modeDateStart >= currentDate && modeDateStart <= oneDayLater) {
      if (!closestDateStart || modeDateStart < closestDateStart) {
        closestDateStart = modeDateStart;
      }
    }

    if (modeDateEnd >= currentDate && modeDateEnd <= oneDayLater) {
      if (!closestDateEnd || modeDateEnd < closestDateEnd) {
        closestDateEnd = modeDateEnd;
      }
    }
  });

  return {
    closestDateStart,
    closestDateEnd
  };
};

const getRemainingTime = (targetDate: Date): number => {
  const currentDate = new Date(Date.now());
  const remainingTime = targetDate.getTime() - currentDate.getTime();
  return Math.max(remainingTime, 0);
};

export function useGetCurrentPromotionMode(): NormalizeMode {
  const [currentMode, setCurrentMode] = useState<NormalizeMode>(() => {
    const modes = generateModes(new Date(Date.now()));

    return modes.length > 0 ? modes[0] : defaultMode;
  });

  const handleChangeStore = () => {
    const modes = generateModes(new Date(Date.now()));
    setCurrentMode(modes.length > 0 ? modes[0] : defaultMode);
  };

  const handleStorageChange = (e: StorageEvent) => {
    if (watchedKeys.includes(e.key || '')) {
      handleChangeStore();
    }
  };

  useEffect(() => {
    const { closestDateStart, closestDateEnd } = getClosestDate(new Date(Date.now()));

    let targetDate: Date | null = null;

    if (closestDateStart || closestDateEnd) {
      if (closestDateStart && closestDateEnd) {
        targetDate =
          new Date(Date.now()) >= closestDateStart && new Date(Date.now()) <= closestDateEnd
            ? closestDateEnd
            : closestDateStart;
      } else if (closestDateStart || closestDateEnd) {
        targetDate = closestDateStart || closestDateEnd;
      }

      if (!targetDate) {
        return;
      }

      const remainingTime = getRemainingTime(targetDate);

      if (remainingTime > 0) {
        const timerId = setTimeout(() => {
          handleChangeStore();
        }, remainingTime);

        return () => {
          clearTimeout(timerId);
        };
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return currentMode;
}
